import { useEffect, useState } from "react";
import { api, fmtEur } from "../lib/api";
import { useApp } from "../lib/store";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Factory, Building2 } from "lucide-react";

export default function PlantView() {
  const { t, hasIntel, picklists } = useApp();
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [filter, setFilter] = useState("all");
  const [segFilter, setSegFilter] = useState("all");

  useEffect(() => {
    if (!hasIntel) return;
    const q = filter === "all" ? "" : `?plant_id=${filter}`;
    api.get(`/plant-supply-rollup${q}`).then((r) => setPlants(r.data.plants)).catch(() => {});
  }, [filter, hasIntel]);

  if (!hasIntel) return <p className="text-sm text-gray-400 py-8 text-center" data-testid="plant-view-denied">{t("access_denied_intel")}</p>;

  const allPlants = plants;
  const bySeg = (p) => segFilter === "all" || (p.segments || []).includes(segFilter);
  const shown = (filter === "all" ? plants.filter((p) => p.customer_count > 0) : plants.filter((p) => p.plant_id === filter)).filter(bySeg);

  return (
    <div className="space-y-5" data-testid="plant-view-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1B2A4A] flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
            <Factory className="h-6 w-6 text-[#F5A623]" />{t("plant_view")}
          </h1>
          <p className="text-sm text-gray-500">{t("plant_view_sub")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={segFilter} onValueChange={setSegFilter}>
            <SelectTrigger className="w-52" data-testid="plant-segment-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("segment")}: {t("all")}</SelectItem>
              {(picklists.segments || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="plant-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("pv_select_plant")}</SelectItem>
              {allPlants.map((p) => <SelectItem key={p.plant_id} value={p.plant_id}>{p.plant_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {shown.map((p) => (
          <div key={p.plant_id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid="plant-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-[#1B2A4A] flex items-center gap-1.5"><Factory className="h-4 w-4" />{p.plant_name}</h3>
                <p className="text-xs text-gray-400">{p.location}</p>
                {(p.segments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.segments.map((s) => <Badge key={s} className="bg-amber-50 text-amber-800 hover:bg-inherit font-normal text-[10px]" data-testid="plant-segment-badge">{s}</Badge>)}
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-lg font-bold font-mono text-[#1B2A4A]">{fmtEur(p.total_revenue)}</p>
                <p className="text-[11px] text-gray-400">{p.customer_count} {t("pv_customers")}</p>
              </div>
            </div>
            {p.customers.length === 0 ? <p className="text-xs text-gray-400 py-2">{t("pv_no_data")}</p> : (
              <div className="divide-y divide-gray-50">
                {p.customers.map((c) => (
                  <button key={c.account_id} onClick={() => navigate(`/accounts/${c.account_id}`)}
                    className="w-full flex items-center gap-2 py-1.5 text-sm hover:bg-amber-50/40 rounded px-1 text-left" data-testid="plant-customer-row">
                    <Building2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="flex-1 truncate">{c.account_name}</span>
                    <span className="text-xs text-gray-400 font-mono">{Number(c.qty).toLocaleString("de-DE")} {c.unit}</span>
                    <span className="font-mono text-sm ml-2">{fmtEur(c.revenue)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
